import axios from 'axios'
import { useAtom } from "jotai"
import {
    useQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import dayjs from 'dayjs'

import type {
    FormMutationOptions,
    CollectionCalendarInfo,
    CollectionCalendarInfoFormInput,
} from '@/types'
import { selectedMunicipalityIdAtom } from "@/state"

export function useCollectionCalendarInfos(municipalityId) {
    return useQuery({
        queryKey: ['collectionCalendarInfos', municipalityId],
        queryFn: async () => {
            const { data } : { data: Array<CollectionCalendarInfo> } = await axios.get(
                '/api/collection_calendar_info/list?municipality_id=' + municipalityId,
            )
            return data
        },
    })
}

export function useCollectionCalendarInfo(collectionCalendarInfoId) {
    return useQuery({
        queryKey: ['collectionCalendarInfo-' + collectionCalendarInfoId],
        queryFn: async () => {
            const { data } : { data: CollectionCalendarInfo } = await axios.get(
                '/api/collection_calendar_info/single?id=' + collectionCalendarInfoId,
            )
            return data
        },
    })
}

export const useCreateCollectionCalendarInfo = (options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const [municipalityId] = useAtom(selectedMunicipalityIdAtom)

    const year = dayjs().year()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (data: CollectionCalendarInfoFormInput) => {            
            return axios.post(`/api/collection_calendar_info/create?municipality_id=${municipalityId}&year=${year}`,
                {description: data.description}
            )
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['collectionCalendarInfos'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        }
    })
}

export const useUpdateCollectionCalendarInfo = (collectionCalendarInfoId, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (data: CollectionCalendarInfoFormInput) => {
            return axios.put(`/api/collection_calendar_info/update?id=${collectionCalendarInfoId}`,
                {description: data.description}
            )
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['collectionCalendarInfos'] })
            queryClient.invalidateQueries({ queryKey: ['collectionCalendarInfo-' + collectionCalendarInfoId] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        }
    })
}

export const useDeleteCollectionCalendarInfo = collectionCalendarInfoId => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => {
            return axios.delete(`/api/collection_calendar_info/delete?id=${collectionCalendarInfoId}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collectionCalendarInfos'] })
        },
    })
}
